/**
 * IndexingStageSheet (TASK-261 — Indexing Pipeline Visualizer)
 *
 * Side panel (Sheet) showing the heavy artifact of a clicked pipeline stage,
 * lazy-loaded by the parent. Sections:
 *   - Вход   (input_summary)           — always
 *   - Выход  (output_artifact as JSON) — always
 *   - Промпт (prompt)                  — LLM stages only
 *   - Ответ  (response)                — LLM stages only
 *
 * Big texts/JSON use the MergedDocumentPage rendering pattern
 * (`font-mono text-xs whitespace-pre-wrap overflow-auto max-h-[70vh]`).
 *
 * For the merge / chains stages we surface a link to the existing dedicated UI
 * (/merged-document, /services) instead of duplicating it. Plain anchors are
 * used (not react-router <Link>) so the component renders without a Router.
 */
import { Loader2, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import type { IndexingStageDetail } from '@/types/indexingRun';

export interface IndexingStageSheetProps {
  stage: IndexingStageDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading?: boolean;
  /**
   * Whether the selected stage is currently executing. While running, the
   * heavy output (output_artifact / response) may not exist yet — we show a
   * "Выполняется…" note instead of an empty block that looks broken.
   */
  running?: boolean;
}

const RUNNING_NOTE = 'Выполняется… результат появится по завершении этапа';

const MONO_BLOCK =
  'font-mono text-xs whitespace-pre-wrap overflow-auto max-h-[70vh] rounded-lg border bg-gray-50 p-3 text-gray-800';

/**
 * Whether a JSON artifact carries no meaningful content yet (running stage):
 * null/undefined, an empty string, an empty array, or an empty object.
 */
function isEmptyArtifact(artifact: unknown): boolean {
  if (artifact == null || artifact === '') return true;
  if (Array.isArray(artifact)) return artifact.length === 0;
  if (typeof artifact === 'object') return Object.keys(artifact).length === 0;
  return false;
}

// A small inline note shown while a running stage has not produced its heavy
// output/response yet. Looks informational (not like an error).
function RunningNote(): JSX.Element {
  return (
    <p
      data-testid="stage-sheet-running-note"
      className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
      {RUNNING_NOTE}
    </p>
  );
}

// Stage machine names → existing dedicated UI route. Matched loosely so a
// rename of the code stage (e.g. "merge" vs "merge_documents") still links.
function dedicatedLink(name: string): { href: string; label: string } | null {
  const n = name.toLowerCase();
  if (n.includes('merge')) {
    return { href: '/merged-document', label: 'Открыть объединённый документ' };
  }
  if (n.includes('chain') || n.includes('service')) {
    return { href: '/services', label: 'Открыть дерево услуг' };
  }
  return null;
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div data-testid={testId} className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
      {children}
    </div>
  );
}

export function IndexingStageSheet({
  stage,
  open,
  onOpenChange,
  loading = false,
  running = false,
}: IndexingStageSheetProps): JSX.Element {
  const isLlm = stage?.kind === 'llm';
  const link = stage ? dedicatedLink(stage.name) : null;

  // The output artifact is "absent" when it's null/undefined, or while running
  // and still empty. In that case we render the running note instead of an
  // empty <pre> that would look like a broken/failed stage.
  const outputAbsent =
    stage != null &&
    (stage.output_artifact == null || (running && isEmptyArtifact(stage.output_artifact)));

  // The LLM response is "absent" when it's an empty string while running.
  const responseAbsent =
    stage != null && running && (stage.response == null || stage.response === '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="stage-sheet"
        className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>{stage?.title ?? 'Этап пайплайна'}</SheetTitle>
          {stage && (
            <SheetDescription>
              {stage.kind === 'llm' ? '🤖 LLM-этап' : '⚙️ Код-этап'}
              {' · '}
              {stage.name}
            </SheetDescription>
          )}
        </SheetHeader>

        {loading ? (
          <div
            data-testid="stage-sheet-loading"
            className="flex items-center justify-center gap-3 py-12 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-sm">Загрузка артефакта…</span>
          </div>
        ) : stage ? (
          <div className="space-y-5">
            {/* Error banner for failed stages */}
            {stage.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {stage.error}
              </div>
            )}

            {/* Link to the dedicated UI for merge / chains stages */}
            {link && (
              <a
                href={link.href}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">
                {link.label}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}

            <Section title="Вход" testId="stage-sheet-input">
              <p className="text-sm text-gray-700">{stage.input_summary}</p>
            </Section>

            <Section title="Выход" testId="stage-sheet-output">
              {outputAbsent ? (
                <RunningNote />
              ) : (
                <pre className={MONO_BLOCK}>
                  {JSON.stringify(stage.output_artifact, null, 2)}
                </pre>
              )}
            </Section>

            {isLlm && stage.prompt && (
              <Section title="Промпт" testId="stage-sheet-prompt">
                <pre className={MONO_BLOCK}>{stage.prompt}</pre>
              </Section>
            )}

            {isLlm && (
              <Section title="Ответ" testId="stage-sheet-response">
                {responseAbsent ? (
                  <RunningNote />
                ) : (
                  <pre className={MONO_BLOCK}>{stage.response}</pre>
                )}
              </Section>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export default IndexingStageSheet;
